import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../contexts/AuthContext';
import { useAccount } from '../contexts/AccountContext';
import { useI18n } from '../i18n/I18nProvider';
import { supabase } from '../lib/supabase';

/** Only the signed-in customer's current invitations, never a staff view-as target. */
export function CustomerInvitations() {
  const { user } = useAuth();
  const { accounts, error: accountError, ready } = useAccount();
  const { t } = useI18n();
  const queryClient = useQueryClient();
  const invitations = useQuery({
    queryKey: ['customer-invitations', user?.id],
    enabled: !!user,
    staleTime: 0,
    refetchOnWindowFocus: 'always',
    refetchInterval: 30_000,
    queryFn: async ({ signal }) => {
      const { data, error } = await supabase.rpc('list_my_customer_invitations').abortSignal(signal);
      if (error) throw error;
      return data ?? [];
    },
  });
  const refresh = async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ['customer-accounts'] }),
      queryClient.invalidateQueries({ queryKey: ['customer-invitations'] }),
    ]);
  };
  const accept = useMutation({
    mutationFn: async (invitation: { customer_user_id: string; invitation_version: number }) => {
      const { data, error } = await supabase.rpc('accept_customer_invitation', {
        p_customer_user_id: invitation.customer_user_id,
        p_invitation_version: invitation.invitation_version,
      });
      if (error || data !== true) throw new Error('Invitation could not be accepted');
    },
    onSettled: refresh,
  });
  const pending = invitations.isError ? [] : invitations.data ?? [];

  return (
    <div className="space-y-3">
      {(accountError || invitations.isError) && (
        <div role="alert" className="rounded-lg border border-border bg-surface p-4 text-sm text-text">
          <p>{t('access.unavailable')}</p>
          <button type="button" onClick={() => void refresh()} className="mt-2 min-h-11 font-medium text-primary underline">
            {t('access.refresh')}
          </button>
        </div>
      )}
      {accept.isError && <p role="alert" className="text-sm text-notification">{t('access.accept_failed')}</p>}
      {pending.length > 0 && (
        <section aria-labelledby="customer-invitations-title" className="rounded-lg border border-border bg-surface p-4 space-y-3">
          <h2 id="customer-invitations-title" className="text-lg font-semibold text-text">{t('access.invitations')}</h2>
          <p className="text-sm text-text-muted">{t('access.invitation_hint')}</p>
          <ul className="space-y-3">
            {pending.map((invitation) => (
              <li key={invitation.customer_user_id} className="flex flex-wrap items-center justify-between gap-3 rounded-md border border-border p-3">
                <span className="font-medium text-text">{invitation.customer_name}</span>
                <button
                  type="button"
                  disabled={accept.isPending}
                  onClick={() => accept.mutate(invitation)}
                  aria-label={t('access.accept_for', { name: invitation.customer_name })}
                  className="min-h-11 rounded-md bg-primary px-4 py-2 text-sm font-medium text-text-inverse disabled:opacity-50"
                >
                  {accept.isPending && accept.variables?.customer_user_id === invitation.customer_user_id
                    ? t('access.accepting') : t('access.accept')}
                </button>
              </li>
            ))}
          </ul>
        </section>
      )}
      {(!ready || invitations.isPending) && accounts.length === 0 && (
        <p role="status" className="text-sm text-text-muted">{t('access.loading')}</p>
      )}
      {ready && !accountError && !invitations.isPending && !invitations.isError && accounts.length === 0 && pending.length === 0 && (
        <div role="status" className="rounded-lg border border-border bg-surface p-4 text-sm text-text-muted">
          <p className="font-medium text-text">{t('account.none')}</p>
          <p className="mt-1">{t('account.none_hint')}</p>
        </div>
      )}
    </div>
  );
}
